import React, { useState } from 'react';
import { Book, Send, Twitter, Copy, Check, Users, Map } from 'lucide-react';
import { DynamicWidget } from "@dynamic-labs/sdk-react-core";

interface SidebarProps {
  currentChapter: number;
  currentView: 'book' | 'roadmap';
  chapterList: any[];
  chapterLockStatus: Record<number, boolean>;
  tokenBalance: number;
  holderCount: number;
  currentRequirements: Record<number, number>;
  onChapterSelect: (chapter: number) => void;
  onViewChange: (view: 'book' | 'roadmap') => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentChapter,
  currentView,
  chapterList,
  chapterLockStatus,
  tokenBalance,
  holderCount,
  currentRequirements,
  onChapterSelect,
  onViewChange
}) => {
  const [copied, setCopied] = useState(false);

  const copyContractAddress = () => {
    navigator.clipboard.writeText('8WKems1g6ihTGkAiNy71Vk81ax24zzkU7BNgCogJpump');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-[250px] fixed h-screen bg-[#0a0a0a] flex flex-col text-white border-r border-gray-800/50">
      {/* Header Section */}
      <div className="p-6 border-b border-gray-800/50 bg-black/30">
        {/* Title with Glowing Effect */}
        <div className="relative mb-6 text-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
            WarLord AI
          </h1>
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 blur-lg -z-10" />
        </div>

        {/* View Selection */}
        <div className="flex gap-2 p-1 mb-4 bg-gray-900/50 rounded-lg backdrop-blur-sm border border-gray-800/50">
          <button
            onClick={() => onViewChange('book')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all duration-300
              ${currentView === 'book'
                ? 'bg-purple-500/20 text-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.2)]'
                : 'text-gray-400 hover:bg-gray-800/50'}`}
          >
            <Book className="w-4 h-4" />
            <span className="text-sm">Read</span>
          </button>
          <button
            onClick={() => onViewChange('roadmap')}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all duration-300
              ${currentView === 'roadmap'
                ? 'bg-purple-500/20 text-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.2)]'
                : 'text-gray-400 hover:bg-gray-800/50'}`}
          >
            <Map className="w-4 h-4" />
            <span className="text-sm">Roadmap</span>
          </button>
        </div>

        {/* Contract Box */}
        <div className="relative group mb-4">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
          <div className="relative flex items-center gap-2 px-3 py-2 bg-black rounded-lg">
            <div className="flex-1 font-mono text-xs text-gray-400 truncate">
              8WKems1g6ihTGkAiNy71Vk81ax24zzkU7BNgCogJpump
            </div>
            <button
              onClick={copyContractAddress}
              className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Dynamic Widget Container */}
        <div className="mb-4 p-3 bg-gray-900/50 rounded-lg border border-gray-800/50 backdrop-blur-sm">
          <DynamicWidget />
          <div className="mt-2 font-mono text-sm">
            <span className="text-gray-500">BALANCE:</span>
            <span className="text-purple-400 ml-2">{tokenBalance.toLocaleString()}</span>
          </div>
        </div>

        {/* Holder Stats */}
        <div className="flex items-center gap-3 p-3 bg-gray-900/30 rounded-lg border border-gray-800/50">
          <div className="p-2 rounded-md bg-green-500/10">
            <Users className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <div className="text-green-400 font-mono text-sm">
              {holderCount.toLocaleString()}
            </div>
            <div className="text-gray-500 text-xs">Active Holders</div>
          </div>
        </div>
      </div>

      {/* Chapter List - Only show when in book view */}
      {currentView === 'book' && (
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <button
                key={i}
                onClick={() => !chapterLockStatus[i + 1] && onChapterSelect(i + 1)}
                disabled={chapterLockStatus[i + 1]}
                className={`w-full group relative ${
                  chapterLockStatus[i + 1] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {/* Highlight for current chapter */}
                {currentChapter === i + 1 && (
                  <div className="absolute inset-0 bg-purple-500/20 rounded-lg blur-sm" />
                )}

                <div className={`relative flex items-start gap-3 p-3 rounded-lg border transition-all duration-300
                  ${currentChapter === i + 1
                    ? 'bg-gray-900/70 border-purple-500/50'
                    : 'bg-gray-900/30 border-gray-800/50 hover:border-gray-700/50'}`}
                >
                  <div className={`p-2 rounded-md ${
                    currentChapter === i + 1
                      ? 'bg-purple-500/20 text-purple-400'
                      : 'bg-gray-800/50 text-gray-500'
                  }`}>
                    <Book className="w-4 h-4" />
                  </div>

                  <div className="flex-1 text-left">
                    <div className={`font-mono text-sm mb-1 ${
                      currentChapter === i + 1 ? 'text-purple-400' : 'text-gray-400'
                    }`}>
                      CHAPTER_{i + 1}
                    </div>
                    <div className="text-sm text-gray-300">
                      {chapterList[i]?.title || `Chapter ${i + 1}`}
                    </div>
                    {chapterLockStatus[i + 1] && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                        <span>Required: {currentRequirements[i + 1].toLocaleString()} tokens</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-gray-800/50 bg-black/30">
        <div className="flex justify-center gap-6">
          <a
            href="https://twitter.com/WarLord_AI"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-purple-400 transition-all"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href="https://t.me/warlordai"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-purple-400 transition-all"
          >
            <Send className="w-5 h-5" />
          </a>
          <a
            href="https://dexscreener.com/solana/brmzxusufhtgbhvmg8efcx6cn3vlak3yk3sq1ttd2fya"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-purple-400 transition-all"
          >
            <img
              src="/dex-screener-seeklogo.svg"
              alt="screener"
              width="20"
              height="20"
              className="opacity-75 hover:opacity-100 transition-opacity"
            />
          </a>
        </div>
      </div>
    </div>
  );
};
